import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import moment from 'moment';
import {
  IGenericServiceResponse,
  IServiceResponse,
} from '../interfaces/IServiceResponse';
import { environment } from 'src/environments/environment';
import { EpisodeOfCareService } from './episodeOfCare.service';
import { PatientService } from './patient.service';
import { IMedicationDetailed } from '../interfaces/Medication/IMedicationDetailed';
import { IAddMedication } from '../interfaces/Medication/IAddMedication';
import { IBrandNameDropdown } from '../interfaces/Medication/IBrandNameDropdown';
import { IAtcDropdown } from '../interfaces/Medication/IAtcDropdown';
import { IActiveSubstanceDropdown } from '../interfaces/Medication/IActiveSubstanceDropdown';
import { IGetList } from '../interfaces/Get/IGetList';
import { IMedicationDropdown } from '../interfaces/Medication/IMedicationDropdown';
import { IScheduledDays } from '../interfaces/Medication/IScheduledDays';
import { TranslateService } from '@ngx-translate/core';
import { SwalToastService } from './swal.service';
import { saveAs } from 'file-saver';
import { date_format, datetime_format } from 'src/date/date.configuration';

const SCHEDULED_DAY_LABELS: Array<{ key: keyof IScheduledDays; label: string }> = [
  { key: 'monday', label: 'Mon' },
  { key: 'tuesday', label: 'Tue' },
  { key: 'wednesday', label: 'Wed' },
  { key: 'thursday', label: 'Thu' },
  { key: 'friday', label: 'Fri' },
  { key: 'saturday', label: 'Sat' },
  { key: 'sunday', label: 'Sun' },
];

@Injectable({ providedIn: 'root' })
export class CurrentMedicationService {
  public medicationOpenSub = new BehaviorSubject<boolean>(false);
  public medicationCreatedSub = new BehaviorSubject<number | null>(null);
  public medicationModalData: {
    carePlanPeriodEnd?: string | Date | null;
    isReadOnly?: boolean;
  } = {};

  public medicationEditSub = new BehaviorSubject<
    IMedicationDetailed | undefined
  >(undefined);

  public currentMedicationDataSub = new BehaviorSubject<
    IGetList<IMedicationDetailed>
  >({});

  public currentMedicationLoadingSub = new BehaviorSubject<boolean>(false);
  public currentMedicationLoadingSub$ =
    this.currentMedicationLoadingSub.asObservable();

  public noKnownLoadingSub = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly _http: HttpClient,
    private readonly eocService: EpisodeOfCareService,
    private readonly patientService: PatientService,
    private readonly translate: TranslateService,
    private readonly swalToastService: SwalToastService
  ) {}

  async getData() {
    this.currentMedicationLoadingSub.next(true);
    this.getCurrentMedication(
      this.patientService.patientIDsignal(),
      this.eocService.currentVisitId()
    ).subscribe({
      next: (response) => {
        this.currentMedicationDataSub.next({
          ...response?.data,
          getAll: response?.data?.getAll?.map((item) =>
            this.enrichMedicationForTable(item)
          ),
        });
        this.currentMedicationLoadingSub.next(false);
        this.noKnownLoadingSub.next(false);
      },
      error: (err) => {
        this.swalToastService.toastError(
          'Unable to retrieve current medications'
        );
        console.error('Error fetching current medications:', err);
      },
    });
  }

  // Builds the display-only fields the medication table needs (period range,
  // scheduled days summary, product name truncated via CSS when it overflows
  // its column) since the backend only sends the raw data these are derived
  // from.
  private enrichMedicationForTable(item: IMedicationDetailed): IMedicationDetailed {
    const result: IMedicationDetailed & { [key: string]: any } = {
      ...item,
      periodCombination: this.getMedicationPeriodLabel(
        item?.onSetDateTime,
        item?.endDateTime
      ),
      scheduledDaysCombination: this.getScheduledDaysLabel(
        item?.scheduledDays
      ),
    };

    if (item?.productCombination) {
      result['__badge__productCombination'] = {
        text: item.productCombination,
        className: 'truncate-cell',
        title: item.productCombination,
      };
    }

    return result;
  }

  private formatMedicationDate(value?: string): string | null {
    if (!value) {
      return null;
    }

    const parsed = moment(
      value,
      [moment.ISO_8601, datetime_format, date_format],
      true
    );
    return parsed.isValid() ? parsed.format(date_format) : value;
  }

  private getMedicationPeriodLabel(
    onSetDateTime?: string,
    endDateTime?: string
  ): string {
    const start = this.formatMedicationDate(onSetDateTime);

    if (!start) {
      return 'N/A';
    }

    const end = this.formatMedicationDate(endDateTime) ?? 'Present';

    return `From ${start} to ${end}`;
  }

  private getScheduledDaysLabel(scheduledDays?: IScheduledDays | null): string {
    const selectedDays = scheduledDays
      ? SCHEDULED_DAY_LABELS.filter((day) => scheduledDays[day.key])
      : [];

    if (selectedDays.length === SCHEDULED_DAY_LABELS.length) {
      return 'Everyday';
    }

    if (selectedDays.length === 0) {
      return 'N/A';
    }

    return selectedDays.map((day) => day.label).join(', ');
  }

  getCurrentMedication(
    patientId: string,
    visitId?: string
  ): Observable<IGenericServiceResponse<IGetList<IMedicationDetailed>>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    if (visitId) {
      return this._http.get<
        IGenericServiceResponse<IGetList<IMedicationDetailed>>
      >(
        `${environment.BACKEND_URL}Medication/CurrentMedications/${patientId}?visitId=${visitId}`,
        {
          headers: headers,
        }
      );
    } else {
      return this._http.get<
        IGenericServiceResponse<IGetList<IMedicationDetailed>>
      >(
        `${environment.BACKEND_URL}Medication/CurrentMedications/${patientId}`,
        {
          headers: headers,
        }
      );
    }
  }

  getMedicationDropdown(): Observable<
    IGenericServiceResponse<IMedicationDropdown>
  > {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this._http.get<IGenericServiceResponse<IMedicationDropdown>>(
      `${environment.BACKEND_URL}Medication/DropdownData`,
      {
        headers: headers,
      }
    );
  }

  getBrandNameDropdown(): Observable<
    IGenericServiceResponse<IBrandNameDropdown>
  > {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this._http.get<IGenericServiceResponse<IBrandNameDropdown>>(
      `${environment.BACKEND_URL}Pharmacy/DropdownData/BrandName`,
      {
        headers: headers,
      }
    );
  }

  getAtcDropdown(): Observable<IGenericServiceResponse<IAtcDropdown>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this._http.get<IGenericServiceResponse<IAtcDropdown>>(
      `${environment.BACKEND_URL}Pharmacy/DropdownData/AtcCode`,
      {
        headers: headers,
      }
    );
  }

  getActiveSubstanceDropdown(): Observable<
    IGenericServiceResponse<IActiveSubstanceDropdown>
  > {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this._http.get<IGenericServiceResponse<IActiveSubstanceDropdown>>(
      `${environment.BACKEND_URL}Pharmacy/DropdownData/ActiveSubstance`,
      {
        headers: headers,
      }
    );
  }

  addMedication(
    currentMedicationsObj: IAddMedication
  ): Observable<IGenericServiceResponse<number>> {
    currentMedicationsObj.visitId = this.eocService.currentVisitId();
    currentMedicationsObj.patientId = this.patientService.patientIDsignal();

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.post<IGenericServiceResponse<number>>(
      `${environment.BACKEND_URL}Medication`,
      currentMedicationsObj,
      {
        headers: headers,
      }
    );
  }

  addNoKnownCurrentMedication() {
    const object = {
      patientId: this.patientService.patientIDsignal(),
      visitId: this.eocService.currentVisitId(),
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.post(
      `${environment.BACKEND_URL}Medication/NoKnownMedications`,
      object,
      {
        headers: headers,
      }
    );
  }

  editMedication(currentMedicationsObj: IAddMedication) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.put(
      `${environment.BACKEND_URL}Medication/${currentMedicationsObj.id}`,
      currentMedicationsObj,
      {
        headers: headers,
      }
    );
  }

  deleteCurrentMedication(
    currentMedicationId: string
  ): Observable<IServiceResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.delete<IServiceResponse>(
      `${environment.BACKEND_URL}Medication/${currentMedicationId}`,
      {
        headers: headers,
      }
    );
  }

  stopCurrentMedication(
    currentMedicationId: string
  ): Observable<IGenericServiceResponse<number>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    const visitId = this.eocService.currentVisitId();

    return this._http.put<IGenericServiceResponse<number>>(
      `${environment.BACKEND_URL}Medication/Stop/${currentMedicationId}/${visitId}`,
      {
        headers: headers,
      }
    );
  }

  deleteNoKnownCurrentMedication(
    currentMedicationId: string
  ): Observable<IServiceResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.delete<IServiceResponse>(
      `${environment.BACKEND_URL}Medication/NoKnownMedications/${currentMedicationId}`,
      {
        headers: headers,
      }
    );
  }

  downloadEPrescription(item: IMedicationDetailed): void {
    this._http
      .get(`${environment.BACKEND_URL}EPrescription/Download/${item?.id}`, {
        responseType: 'blob',
      })
      .subscribe({
        next: (blob) => {
          const file = new Blob([blob], { type: blob.type });
          saveAs(file, `eprescription-${item?.id}.json`);
        },
        error: (err) => {
          this.swalToastService.toastError(
            'Unable to download the e-prescription'
          );
          console.error('Error downloading e-prescription:', err);
        },
      });
  }
}
